import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { formatTime } from '../utils/dateFormatting';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import {
  ChevronLeft,
  ChevronRight,
  Truck,
  Clock,
  Search,
  Maximize2,
  Minimize2,
  Filter,
  ChevronDown,
  ExternalLink,
  Building2,
  Loader2,
  MapPin,
  User,
  FileText,
  Hash,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, getDaysInMonth, startOfMonth, getDay, addMonths, subMonths } from 'date-fns';

// ============================================
// CONSTANTS
// ============================================
const MIN_DAY_WIDTH = 36;
const ROW_HEIGHT = 48;
const ASSET_COL_WIDTH = 260;

const STATUS_COLORS = {
  allocated: { bg: 'bg-amber-500/70', border: 'border-amber-600', text: 'text-amber-700 dark:text-amber-300', bar: '#f59e0b' },
  confirmed: { bg: 'bg-blue-500/70', border: 'border-blue-600', text: 'text-blue-700 dark:text-blue-300', bar: '#3b82f6' },
  docketed: { bg: 'bg-purple-500/70', border: 'border-purple-600', text: 'text-purple-700 dark:text-purple-300', bar: '#a855f7' },
  invoiced: { bg: 'bg-green-500/70', border: 'border-green-600', text: 'text-green-700 dark:text-green-300', bar: '#22c55e' },
};

const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.allocated;

// ============================================
// MAIN COMPONENT
// ============================================
export default function Utilisation() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State - initialize from URL params if present
  const [currentDate, setCurrentDate] = useState(() => {
    const y = searchParams.get('year');
    const m = searchParams.get('month');
    if (y && m) return new Date(parseInt(y), parseInt(m) - 1, 1);
    return startOfMonth(new Date());
  });
  const [loading, setLoading] = useState(true);
  const [equipment, setEquipment] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [ownSupplierId, setOwnSupplierId] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [timeFormat, setTimeFormat] = useState('24h');

  // Responsive day width
  const ganttContainerRef = useRef(null);
  const [dayWidth, setDayWidth] = useState(MIN_DAY_WIDTH);

  // Filters
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [supplierFilterOpen, setSupplierFilterOpen] = useState(false);
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);
  const [defaultFilterApplied, setDefaultFilterApplied] = useState(false);

  // Selection
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Full screen
  const [isFullScreen, setIsFullScreen] = useState(false);
  const containerRef = useRef(null);

  // Scroll sync
  const ganttScrollRef = useRef(null);
  const assetScrollRef = useRef(null);

  // ============================================
  // DATA FETCHING
  // ============================================
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const { data } = await api.get(`/utilisation/data?year=${year}&month=${month}`);
      setEquipment(data.equipment || []);
      setSuppliers(data.suppliers || []);
      setEquipmentTypes(data.equipment_types || []);
      setAllocations(data.allocations || []);
      setOwnSupplierId(data.own_supplier_id);
      setDaysInMonth(data.days_in_month);
      setDateFormat(data.tenant_settings?.date_format || 'DD/MM/YYYY');
      setTimeFormat(data.tenant_settings?.time_format || '24h');

      // Default filter: own supplier (only on first load)
      if (!defaultFilterApplied && data.own_supplier_id) {
        setSelectedSuppliers([data.own_supplier_id]);
        setDefaultFilterApplied(true);
      }
    } catch (err) {
      toast.error('Failed to load utilisation data');
    } finally {
      setLoading(false);
    }
  }, [api, currentDate, defaultFilterApplied]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Responsive day width: expand to fill available space
  useEffect(() => {
    const el = ganttContainerRef.current;
    if (!el) return;
    // Immediately calculate on mount
    const availableWidth = el.getBoundingClientRect().width - 8;
    setDayWidth(Math.max(MIN_DAY_WIDTH, Math.floor(availableWidth / daysInMonth)));
    // Then watch for resizes
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const w = entry.contentRect.width - 8;
        setDayWidth(Math.max(MIN_DAY_WIDTH, Math.floor(w / daysInMonth)));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [daysInMonth, loading]);

  // Date formatter using tenant setting
  const formatDateDisplay = useCallback((dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    switch (dateFormat) {
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return dateStr;
      case 'DD/MM/YYYY':
      default: return `${day}/${month}/${year}`;
    }
  }, [dateFormat]);

  // ============================================
  // FILTERING
  // ============================================
  const supplierMap = useMemo(() => {
    const m = {};
    suppliers.forEach(s => { m[s.id] = s.company_name; });
    return m;
  }, [suppliers]);

  const filteredEquipment = useMemo(() => {
    let list = [...equipment];
    if (selectedSuppliers.length > 0) {
      list = list.filter(e => selectedSuppliers.includes(e.supplier_id));
    }
    if (selectedTypes.length > 0) {
      list = list.filter(e => selectedTypes.includes(e.equipment_type));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(e =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.asset_id || '').toLowerCase().includes(q) ||
        (e.equipment_type || '').toLowerCase().includes(q)
      );
    }
    // Sort: own supplier first, then alphabetical
    list.sort((a, b) => {
      const aOwn = a.supplier_id === ownSupplierId ? 0 : 1;
      const bOwn = b.supplier_id === ownSupplierId ? 0 : 1;
      if (aOwn !== bOwn) return aOwn - bOwn;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [equipment, selectedSuppliers, selectedTypes, searchQuery, ownSupplierId]);

  // Group allocations by equipment_id
  const allocationsByEquipment = useMemo(() => {
    const map = {};
    allocations.forEach(a => {
      if (!map[a.equipment_id]) map[a.equipment_id] = [];
      map[a.equipment_id].push(a);
    });
    return map;
  }, [allocations]);

  // ============================================
  // MONTH NAVIGATION
  // ============================================
  const handlePrevMonth = () => setCurrentDate(d => subMonths(d, 1));
  const handleNextMonth = () => setCurrentDate(d => addMonths(d, 1));
  const handleToday = () => setCurrentDate(startOfMonth(new Date()));

  // ============================================
  // FULL SCREEN
  // ============================================
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };
  useEffect(() => {
    const handler = () => setIsFullScreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const openInNewTab = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    window.open(`/utilisation?year=${year}&month=${month}`, '_blank');
  };

  // ============================================
  // SCROLL SYNC
  // ============================================
  const handleGanttScroll = (e) => {
    if (assetScrollRef.current) {
      assetScrollRef.current.scrollTop = e.target.scrollTop;
    }
  };
  const handleAssetScroll = (e) => {
    if (ganttScrollRef.current) {
      ganttScrollRef.current.scrollTop = e.target.scrollTop;
    }
  };

  // ============================================
  // DAY COLUMNS
  // ============================================
  const days = useMemo(() => {
    const arr = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dow = getDay(date);
      arr.push({
        day: d,
        dateStr: format(date, 'yyyy-MM-dd'),
        dayLabel: format(date, 'EEE'),
        isWeekend: dow === 0 || dow === 6,
        isToday: format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'),
      });
    }
    return arr;
  }, [currentDate, daysInMonth]);

  // ============================================
  // BAR CALCULATIONS
  // ============================================
  const getBarPosition = useCallback((alloc) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month, daysInMonth);

    const allocStart = new Date(alloc.start_date + 'T00:00:00');
    const allocEnd = new Date(alloc.end_date + 'T00:00:00');

    const barStart = allocStart < monthStart ? monthStart : allocStart;
    const barEnd = allocEnd > monthEnd ? monthEnd : allocEnd;

    const startDay = barStart.getDate();
    const endDay = barEnd.getDate();

    const left = (startDay - 1) * dayWidth;
    const width = (endDay - startDay + 1) * dayWidth - 2;

    return { left, width: Math.max(width, dayWidth - 2), startDay, endDay };
  }, [currentDate, daysInMonth, dayWidth]);

  // For multiple bars per day per asset, stack them
  const getEquipmentBars = useCallback((equipId) => {
    const allocs = allocationsByEquipment[equipId] || [];
    if (allocs.length === 0) return [];

    // Sort by start_date
    const sorted = [...allocs].sort((a, b) => a.start_date.localeCompare(b.start_date));

    // Simple lane assignment (greedy): find first lane where bar doesn't overlap
    const lanes = [];
    return sorted.map(alloc => {
      const pos = getBarPosition(alloc);
      let lane = 0;
      for (let i = 0; i < lanes.length; i++) {
        if (pos.startDay > lanes[i]) { lane = i; break; }
        lane = i + 1;
      }
      if (lane >= lanes.length) lanes.push(pos.endDay);
      else lanes[lane] = pos.endDay;
      
      // Resolve effective status for the bar
      const dayStatuses = alloc.day_statuses || {};
      const allocStatus = alloc.status || 'draft';
      const jobStatus = alloc.job_status;
      let effectiveStatus = jobStatus || allocStatus;
      
      // Terminal job statuses take precedence — no per-day aggregation needed
      if (jobStatus !== 'docketed' && jobStatus !== 'invoiced') {
        // For non-terminal statuses, aggregate day_statuses to distinguish confirmed vs allocated
        if (Object.keys(dayStatuses).length > 0 || allocStatus) {
          const year = currentDate.getFullYear();
          const month = currentDate.getMonth();
          const statuses = new Set();
          for (let d = pos.startDay; d <= pos.endDay; d++) {
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const ds = dayStatuses[dateStr] || allocStatus;
            statuses.add(ds === 'confirmed' ? 'confirmed' : 'allocated');
          }
          if (statuses.size === 1) {
            effectiveStatus = [...statuses][0];
          }
        }
      }
      
      return { ...alloc, ...pos, lane, status: effectiveStatus };
    });
  }, [allocationsByEquipment, getBarPosition]);

  // Calculate row height based on max lanes
  const rowHeights = useMemo(() => {
    const heights = {};
    filteredEquipment.forEach(eq => {
      const bars = getEquipmentBars(eq.id);
      const maxLane = bars.reduce((max, b) => Math.max(max, b.lane), -1);
      heights[eq.id] = Math.max(ROW_HEIGHT, (maxLane + 1) * 28 + 20);
    });
    return heights;
  }, [filteredEquipment, getEquipmentBars]);

  // ============================================
  // SUPPLIER FILTER TOGGLE
  // ============================================
  const toggleSupplier = (id) => {
    setSelectedSuppliers(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };
  const toggleType = (name) => {
    setSelectedTypes(prev =>
      prev.includes(name) ? prev.filter(t => t !== name) : [...prev, name]
    );
  };

  // Get unique equipment types that are actually used by equipment
  const usedEquipmentTypes = useMemo(() => {
    const used = new Set(equipment.map(e => e.equipment_type).filter(Boolean));
    return equipmentTypes.filter(t => used.has(t.name)).sort((a, b) => a.name.localeCompare(b.name));
  }, [equipment, equipmentTypes]);

  // ============================================
  // RENDER
  // ============================================
  return (
    <TooltipProvider delayDuration={200}>
      <div ref={containerRef} className={`flex flex-col h-full overflow-x-hidden ${isFullScreen ? 'bg-background p-4' : ''}`} data-testid="utilisation-page">
        {/* HEADER */}
        <div className="flex items-center justify-between gap-4 mb-4 px-1 flex-wrap" data-testid="utilisation-header">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/schedule')} data-testid="back-to-schedule-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back to Schedule
            </Button>
            <div className="flex items-center gap-1 bg-muted rounded-lg px-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handlePrevMonth} data-testid="prev-month-btn">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="font-semibold text-sm min-w-[120px] text-center" data-testid="current-month-label">
                {format(currentDate, 'MMMM yyyy')}
              </span>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleNextMonth} data-testid="next-month-btn">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={handleToday}>Today</Button>
          </div>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search assets..."
                className="h-8 pl-7 w-40 text-xs"
                data-testid="asset-search-input"
              />
            </div>

            {/* Supplier Filter */}
            <Popover open={supplierFilterOpen} onOpenChange={setSupplierFilterOpen} modal={true}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="supplier-filter-btn">
                  <Building2 className="h-3.5 w-3.5" />
                  Supplier {selectedSuppliers.length > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{selectedSuppliers.length}</Badge>}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="end" container={containerRef.current}>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {suppliers.map(s => (
                    <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-xs">
                      <Checkbox checked={selectedSuppliers.includes(s.id)} onCheckedChange={() => toggleSupplier(s.id)} />
                      <span className="truncate">{s.company_name}</span>
                      {s.is_own_company && <Badge variant="outline" className="text-[9px] h-4 px-1 ml-auto shrink-0">Own</Badge>}
                    </label>
                  ))}
                </div>
                {selectedSuppliers.length > 0 && (
                  <Button variant="ghost" size="sm" className="w-full mt-1 h-7 text-xs" onClick={() => setSelectedSuppliers([])}>
                    Clear All
                  </Button>
                )}
              </PopoverContent>
            </Popover>

            {/* Type Filter */}
            <Popover open={typeFilterOpen} onOpenChange={setTypeFilterOpen} modal={true}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="type-filter-btn">
                  <Truck className="h-3.5 w-3.5" />
                  Type {selectedTypes.length > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{selectedTypes.length}</Badge>}
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="end" container={containerRef.current}>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {usedEquipmentTypes.map(t => (
                    <label key={t.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-xs">
                      <Checkbox checked={selectedTypes.includes(t.name)} onCheckedChange={() => toggleType(t.name)} />
                      <span className="truncate">{t.name}</span>
                    </label>
                  ))}
                </div>
                {selectedTypes.length > 0 && (
                  <Button variant="ghost" size="sm" className="w-full mt-1 h-7 text-xs" onClick={() => setSelectedTypes([])}>
                    Clear All
                  </Button>
                )}
              </PopoverContent>
            </Popover>

            <div className="h-6 w-px bg-border" />

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={openInNewTab} data-testid="open-new-tab-btn">
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open in new tab</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={toggleFullScreen} data-testid="fullscreen-btn">
                  {isFullScreen ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{isFullScreen ? 'Exit Full Screen' : 'Full Screen'}</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* STATUS LEGEND */}
        <div className="flex items-center gap-3 mb-3 px-1 text-[10px]">
          {Object.entries(STATUS_COLORS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className={`w-3 h-2.5 rounded-sm ${val.bg} border ${val.border}`} />
              <span className="capitalize text-muted-foreground">{key.replace('_', ' ')}</span>
            </div>
          ))}
          <span className="text-muted-foreground ml-2">{filteredEquipment.length} assets</span>
        </div>

        {/* MAIN GANTT AREA */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex-1 flex border rounded-lg overflow-hidden bg-background min-w-0" style={{ minHeight: 0 }}>
            {/* LEFT: Asset List (sticky) */}
            <div
              className="border-r flex-shrink-0 flex flex-col"
              style={{ width: ASSET_COL_WIDTH }}
            >
              {/* Asset header */}
              <div className="h-10 border-b bg-muted/50 flex items-center px-3 text-xs font-semibold text-muted-foreground shrink-0">
                Assets ({filteredEquipment.length})
              </div>
              {/* Asset rows */}
              <div
                ref={assetScrollRef}
                className="flex-1 overflow-y-auto overflow-x-hidden"
                onScroll={handleAssetScroll}
                style={{ scrollbarWidth: 'none' }}
              >
                {filteredEquipment.length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">No assets match filters</div>
                ) : (
                  filteredEquipment.map(eq => (
                    <div
                      key={eq.id}
                      className="flex items-center gap-2 px-3 border-b hover:bg-muted/30 transition-colors"
                      style={{ height: rowHeights[eq.id] || ROW_HEIGHT }}
                      data-testid={`asset-row-${eq.id}`}
                    >
                      <Truck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium truncate">{eq.asset_id || eq.equipment_type}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {eq.equipment_type}{eq.capacity ? ` - ${eq.capacity}` : ''}
                        </div>
                        <div className="text-[10px] text-muted-foreground/60 truncate">
                          {supplierMap[eq.supplier_id] || 'No supplier'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* CENTRE: Gantt Chart */}
            <div ref={ganttContainerRef} className="flex-1 flex flex-col min-w-0">
              {/* Day headers */}
              <div className="h-10 border-b bg-muted/50 overflow-hidden shrink-0">
                <div
                  className="flex h-full"
                  style={{ width: daysInMonth * dayWidth, transform: ganttScrollRef.current ? `translateX(-${ganttScrollRef.current.scrollLeft}px)` : undefined }}
                  id="day-header-row"
                >
                  {days.map(d => (
                    <div
                      key={d.day}
                      className={`flex flex-col items-center justify-center shrink-0 border-r text-[10px] ${
                        d.isToday ? 'bg-primary/10 font-bold text-primary' : d.isWeekend ? 'bg-muted/80 text-muted-foreground/60' : 'text-muted-foreground'
                      }`}
                      style={{ width: dayWidth }}
                    >
                      <span>{d.dayLabel}</span>
                      <span className="font-semibold text-[11px]">{d.day}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gantt rows */}
              <div
                ref={ganttScrollRef}
                className="flex-1 overflow-auto"
                onScroll={(e) => {
                  handleGanttScroll(e);
                  // Sync day header
                  const header = document.getElementById('day-header-row');
                  if (header) header.style.transform = `translateX(-${e.target.scrollLeft}px)`;
                }}
              >
                <div style={{ width: daysInMonth * dayWidth, minWidth: '100%' }}>
                  {filteredEquipment.map(eq => {
                    const bars = getEquipmentBars(eq.id);
                    const rh = rowHeights[eq.id] || ROW_HEIGHT;
                    return (
                      <div
                        key={eq.id}
                        className="relative border-b"
                        style={{ height: rh }}
                      >
                        {/* Day column grid lines */}
                        {days.map(d => (
                          <div
                            key={d.day}
                            className={`absolute top-0 bottom-0 border-r ${
                              d.isToday ? 'bg-primary/5' : d.isWeekend ? 'bg-muted/40' : ''
                            }`}
                            style={{ left: (d.day - 1) * dayWidth, width: dayWidth }}
                          />
                        ))}
                        {/* Bars */}
                        {bars.map((bar) => {
                          const sc = getStatusColor(bar.status);
                          return (
                            <Tooltip key={bar.id}>
                              <TooltipTrigger asChild>
                                <button
                                  className={`absolute rounded-md ${sc.bg} border ${sc.border} cursor-pointer hover:brightness-110 transition-all flex items-center px-1.5 overflow-hidden`}
                                  style={{
                                    left: bar.left + 1,
                                    width: bar.width,
                                    top: 4 + bar.lane * 28,
                                    height: 24,
                                  }}
                                  onClick={() => { setSelectedAllocation(bar); setDetailOpen(true); }}
                                  data-testid={`gantt-bar-${bar.id}`}
                                >
                                  <span className="text-[10px] font-medium truncate whitespace-nowrap">
                                    {bar.job_number ? `#${bar.job_number}` : ''}{bar.customer_name ? ` ${bar.customer_name}` : ''}
                                  </span>
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="text-xs max-w-64">
                                <div className="font-semibold">Job #{bar.job_number}</div>
                                {bar.customer_name && <div>{bar.customer_name}</div>}
                                {bar.site_name && <div className="text-muted-foreground">{bar.site_name}</div>}
                                <div className="text-muted-foreground">{formatDateDisplay(bar.start_date)} - {formatDateDisplay(bar.end_date)}</div>
                                <div className="capitalize">{(bar.status || '').replace('_', ' ')}</div>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RIGHT: Job Detail Panel */}
        <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
          <SheetContent className="w-[380px] sm:w-[420px] overflow-y-auto" data-testid="job-detail-panel">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                {selectedAllocation?.job_number && (
                  <Badge variant="outline" className="font-mono">#{selectedAllocation.job_number}</Badge>
                )}
                Job Details
              </SheetTitle>
            </SheetHeader>
            {selectedAllocation && (
              <div className="space-y-5 mt-4">
                {/* Status */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
                  <Badge className={`${getStatusColor(selectedAllocation.status).bg} ${getStatusColor(selectedAllocation.status).text} border ${getStatusColor(selectedAllocation.status).border} capitalize`}>
                    {(selectedAllocation.status || 'Not Allocated').replace('_', ' ')}
                  </Badge>
                </div>

                {/* Customer */}
                {selectedAllocation.customer_name && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Building2 className="h-3 w-3" /> Customer</Label>
                    <p className="text-sm font-medium">{selectedAllocation.customer_name}</p>
                  </div>
                )}

                {/* Site */}
                {selectedAllocation.site_name && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Site</Label>
                    <p className="text-sm">{selectedAllocation.site_name}</p>
                  </div>
                )}

                {/* Description */}
                {selectedAllocation.description && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Description</Label>
                    <p className="text-sm">{selectedAllocation.description}</p>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">Start Date</Label>
                    <p className="text-sm font-medium">{formatDateDisplay(selectedAllocation.start_date)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 block">End Date</Label>
                    <p className="text-sm font-medium">{formatDateDisplay(selectedAllocation.end_date)}</p>
                  </div>
                </div>

                {/* Times */}
                <div className="grid grid-cols-2 gap-3">
                  {selectedAllocation.start_time && (
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Start Time</Label>
                      <p className="text-sm font-medium">{formatTime(selectedAllocation.start_time, timeFormat)}</p>
                    </div>
                  )}
                  {selectedAllocation.end_time && (
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> End Time</Label>
                      <p className="text-sm font-medium">{formatTime(selectedAllocation.end_time, timeFormat)}</p>
                    </div>
                  )}
                </div>

                {/* Depot Time */}
                {selectedAllocation.depot_time && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Depot Time</Label>
                    <p className="text-sm font-medium text-[#c9a227]">{selectedAllocation.depot_time}</p>
                  </div>
                )}

                {/* Operator */}
                {selectedAllocation.operator_name && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><User className="h-3 w-3" /> Operator</Label>
                    <p className="text-sm">{selectedAllocation.operator_name}</p>
                  </div>
                )}

                {/* PO Number */}
                {selectedAllocation.po_number && (
                  <div>
                    <Label className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Hash className="h-3 w-3" /> PO Number</Label>
                    <p className="text-sm">{selectedAllocation.po_number}</p>
                  </div>
                )}

                {/* Allocation Dates (if different from job dates) */}
                {(selectedAllocation.job_start_date || selectedAllocation.job_end_date) && (
                  <div className="border-t pt-3">
                    <Label className="text-xs text-muted-foreground mb-2 block font-semibold">Full Job Duration</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[10px] text-muted-foreground/70 mb-0.5 block">Job Start</Label>
                        <p className="text-xs">{formatDateDisplay(selectedAllocation.job_start_date) || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground/70 mb-0.5 block">Job End</Label>
                        <p className="text-xs">{formatDateDisplay(selectedAllocation.job_end_date) || 'Ongoing'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </TooltipProvider>
  );
}
